import {
  ArrowDownOnSquareIcon,
  BookmarkIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Save } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/Button';
import { DocumentListItem } from '@/components/documents/DocumentListItem';
import { getFileType } from '@/components/documents/DocumentListItem';
import { DocumentUploadZone } from '@/components/documents/DocumentUploadZone';
import FileViewer from '@/components/FileViewer';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { NoData } from '@/components/ui/NoData';
import { useDeleteFile, useDownloadFile, useMy, useUploadFile } from '@/hooks';
import { MyFile, ParsedFileTypes } from '@/types';
import { useStorage } from '@/utils/storage/useStorage.util';

interface DocumentsSectionsProps {
  query?: string;
  customerEmail?: string;
  enableUpload?: boolean;
  enableSearch?: boolean;
  enableFavorites?: boolean;
  sharedWithLabel?: string;
  sharedByLabel?: string;
  sharedWithDescription?: string;
  sharedByDescription?: string;
  isPraetorianUser?: boolean; // Determines which section can upload
}

export const DocumentsSections: React.FC<DocumentsSectionsProps> = ({
  query = 'home',
  customerEmail,
  enableUpload = true,
  enableSearch = true,
  enableFavorites = true,
  sharedWithLabel = 'Documents Shared with Customer',
  sharedByLabel = 'Documents Shared by Customer',
  sharedWithDescription = 'Files and reports that have been shared with the customer for review and analysis.',
  sharedByDescription = 'Files and data that the customer has provided for analysis and security assessment.',
  isPraetorianUser = true, // Default to Praetorian user for backward compatibility
}) => {
  // Role-based upload permissions
  const canUploadToSharedWith = isPraetorianUser && enableUpload;
  const canUploadToSharedBy = !isPraetorianUser && enableUpload;
  const { mutateAsync: uploadFile } = useUploadFile(customerEmail);
  const { mutate: downloadFile } = useDownloadFile(customerEmail);
  const { mutateAsync: deleteFile } = useDeleteFile();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showFileUpload, setShowFileUpload] = useState<
    'shared-with' | 'shared-by' | null
  >(null);
  const [filename, setFilename] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [originalContent, setOriginalContent] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useStorage<string[]>(
    { key: 'files/favorites' },
    []
  );

  const {
    data: sharedByCustomerFiles = [],
    invalidate: invalidateSharedByCustomerFiles,
    status: sharedByCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-by`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const {
    data: sharedWithCustomerFiles = [],
    invalidate: invalidateSharedWithCustomerFiles,
    status: sharedWithCustomerFilesStatus,
  } = useMy(
    {
      resource: 'file',
      query: `${query}/shared-with`,
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  // Query PlexTrac reports stored in reports/ directory
  const {
    data: plextracReports = [],
    invalidate: invalidatePlextracReports,
    status: plextracReportsStatus,
  } = useMy(
    {
      resource: 'file',
      query: 'reports/',
    },
    {
      email: customerEmail,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always' as const,
    }
  );

  const files = useMemo(() => {
    return [
      ...sharedByCustomerFiles,
      ...sharedWithCustomerFiles,
      ...plextracReports,
    ];
  }, [sharedByCustomerFiles, sharedWithCustomerFiles, plextracReports]);

  const filterFilesBySearch = (fileList: MyFile[]) =>
    fileList.filter(file =>
      file.name.toLowerCase().includes(search.toLowerCase())
    );

  const onFilesDrop = (
    files: { content: string | ArrayBuffer; file: File }[],
    section: 'shared-with' | 'shared-by'
  ) => {
    files.forEach(({ content, file }) => {
      setUploadProgress(0);
      uploadFile({
        name: `${query}/${section}/${file.name}`,
        content: content ?? '',
        onProgress: progressEvent => {
          const progress =
            (progressEvent.loaded / (progressEvent.total ?? 1)) * 100;
          setUploadProgress(progress >= 100 ? null : progress);
        },
      })
        .then(() => {
          setUploadProgress(null);
          setShowFileUpload(null);
          invalidateSharedWithCustomerFiles();
          invalidateSharedByCustomerFiles();
          invalidatePlextracReports();
        })
        .catch(() => {
          setUploadProgress(null);
        });
    });
  };

  const handleDownload = (file: MyFile) => {
    downloadFile({ name: file.name });
  };

  const handleToggleFavorite = (file: MyFile) => {
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    } else {
      setFavorites([...favorites, file.name]);
    }
  };

  const handleDelete = async (file: MyFile) => {
    await deleteFile({ name: file.name });
    // Remove from favorites if it was favorited
    if (favorites.includes(file.name)) {
      setFavorites(favorites.filter(fav => fav !== file.name));
    }
    // Invalidate all file queries
    invalidateSharedWithCustomerFiles();
    invalidateSharedByCustomerFiles();
    invalidatePlextracReports();
  };

  const fileType = filename ? getFileType(filename) : undefined;
  const hasUnsavedChanges =
    content !== null && originalContent !== null && content !== originalContent;

  return (
    <div className="flex h-full flex-col gap-6 overflow-auto pr-4">
      {enableSearch && (
        <>
          <div className="flex items-center justify-between">
            <div className="relative max-w-md grow">
              <input
                placeholder="Search documents..."
                value={search}
                name="file_search"
                onChange={e => setSearch(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-primary h-10 w-full rounded-md border px-4 pl-10"
              />
              <MagnifyingGlassIcon className="text-chariot-text-secondary absolute top-2.5 left-3 size-5" />
              {search.length > 0 && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute top-2.5 right-3"
                >
                  <XMarkIcon className="text-chariot-text-secondary size-5" />
                </button>
              )}
            </div>
          </div>

          <div className="bg-chariot-stroke h-px w-full"></div>
        </>
      )}

      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <h2 className="text-lg font-bold">{sharedWithLabel}</h2>
            <p className="text-chariot-text-secondary mt-2">
              {sharedWithDescription}
            </p>
          </div>
          {canUploadToSharedWith && (
            <Button
              styleType="primary"
              startIcon={<DocumentPlusIcon className="size-5" />}
              label="Upload File"
              onClick={() =>
                setShowFileUpload(
                  showFileUpload === 'shared-with' ? null : 'shared-with'
                )
              }
            />
          )}
        </div>

        {showFileUpload === 'shared-with' && (
          <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-6">
            <DocumentUploadZone
              onFilesDrop={files => onFilesDrop(files, 'shared-with')}
              uploadProgress={uploadProgress}
              subTitle="Upload documents to share with the customer"
            />
          </div>
        )}

        <div className="border-chariot-stroke rounded-md border">
          <Loader
            isLoading={
              sharedWithCustomerFilesStatus === 'pending' ||
              plextracReportsStatus === 'pending'
            }
          >
            <div className="divide-chariot-stroke divide-y">
              {filterFilesBySearch([
                ...sharedWithCustomerFiles,
                ...plextracReports,
              ]).length > 0 ? (
                filterFilesBySearch([
                  ...sharedWithCustomerFiles,
                  ...plextracReports,
                ]).map(file => (
                  <DocumentListItem
                    key={file.name}
                    file={file}
                    onView={() => setFilename(file.name)}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    isFavorite={
                      enableFavorites && favorites.includes(file.name)
                    }
                    onToggleFavorite={
                      enableFavorites ? handleToggleFavorite : undefined
                    }
                    displayName={file.name
                      .replace(`${query}/`, '')
                      .replace('reports/', '')}
                  />
                ))
              ) : (
                <div className="p-8">
                  <NoData
                    title="No documents shared with customer"
                    description="Upload files to share reports and data with your customer."
                  />
                </div>
              )}
            </div>
          </Loader>
        </div>
      </div>

      <div className="bg-chariot-stroke h-px w-full"></div>

      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col">
            <h2 className="text-lg font-bold">{sharedByLabel}</h2>
            <p className="text-chariot-text-secondary mt-2">
              {sharedByDescription}
            </p>
          </div>
          {canUploadToSharedBy && (
            <Button
              styleType="primary"
              startIcon={<DocumentPlusIcon className="size-5" />}
              label="Upload File"
              onClick={() =>
                setShowFileUpload(
                  showFileUpload === 'shared-by' ? null : 'shared-by'
                )
              }
            />
          )}
        </div>

        {showFileUpload === 'shared-by' && (
          <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-6">
            <DocumentUploadZone
              onFilesDrop={files => onFilesDrop(files, 'shared-by')}
              uploadProgress={uploadProgress}
              subTitle="Upload documents received from the customer"
            />
          </div>
        )}

        <div className="border-chariot-stroke rounded-md border">
          <Loader isLoading={sharedByCustomerFilesStatus === 'pending'}>
            <div className="divide-chariot-stroke divide-y">
              {filterFilesBySearch(sharedByCustomerFiles).length > 0 ? (
                filterFilesBySearch(sharedByCustomerFiles).map(file => (
                  <DocumentListItem
                    key={file.name}
                    file={file}
                    onView={() => setFilename(file.name)}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    isFavorite={
                      enableFavorites && favorites.includes(file.name)
                    }
                    onToggleFavorite={
                      enableFavorites ? handleToggleFavorite : undefined
                    }
                    displayName={file.name.replace(`${query}/`, '')}
                  />
                ))
              ) : (
                <div className="p-8">
                  <NoData
                    title="No documents from customer"
                    description="Files shared by the customer will appear here."
                  />
                </div>
              )}
            </div>
          </Loader>
        </div>
      </div>

      <Modal
        open={filename.length > 0}
        onClose={() => {
          setFilename('');
          setContent(null);
          setOriginalContent(null);
        }}
        size="6xl"
        title={
          <div className="flex w-full items-center justify-between">
            <span>{filename}</span>
            <div className="mr-8 flex items-center space-x-2">
              {fileType === ParsedFileTypes.DOCUMENT && (
                <Tooltip
                  title={
                    hasUnsavedChanges
                      ? 'Save Unsaved Changes'
                      : 'Save and Overwrite Existing'
                  }
                >
                  <div className="relative">
                    <Button
                      startIcon={<Save className="size-5" />}
                      onClick={async () => {
                        uploadFile({
                          ignoreSnackbar: true,
                          name: filename,
                          content: content ?? '',
                        }).then(() => {
                          setOriginalContent(content);
                        });
                      }}
                      styleType="none"
                      className="hover:bg-chariot-background-secondary rounded-sm p-2"
                    />
                    {hasUnsavedChanges && (
                      <div className="border-chariot-background-primary absolute -top-1 -right-1 size-3 rounded-full border-2 bg-red-500"></div>
                    )}
                  </div>
                </Tooltip>
              )}
              <Tooltip title="Download">
                <Button
                  startIcon={<ArrowDownOnSquareIcon className="size-5" />}
                  onClick={() => {
                    const file = files.find(f => f.name === filename);
                    if (file) handleDownload(file);
                  }}
                  styleType="none"
                  className="hover:bg-chariot-background-secondary rounded-sm p-2"
                />
              </Tooltip>
              {enableFavorites && (
                <Tooltip
                  title={
                    favorites.includes(filename)
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }
                >
                  <Button
                    startIcon={
                      <BookmarkIcon
                        className={`size-5 ${favorites.includes(filename) ? 'fill-current text-yellow-500' : ''}`}
                      />
                    }
                    onClick={() => {
                      if (favorites.includes(filename)) {
                        setFavorites(favorites.filter(fav => fav !== filename));
                      } else {
                        setFavorites([...favorites, filename]);
                      }
                    }}
                    styleType="none"
                    className="hover:bg-chariot-background-secondary rounded-sm p-2"
                  />
                </Tooltip>
              )}
            </div>
          </div>
        }
        footer={undefined}
      >
        <FileViewer
          fileName={filename}
          email={customerEmail}
          onChange={changed => {
            setContent(changed);
            if (originalContent === null) {
              setOriginalContent(changed);
            }
          }}
          onClose={() => {
            setFilename('');
            setContent(null);
            setOriginalContent(null);
          }}
        />
      </Modal>
    </div>
  );
};
